import {useState} from 'react'
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {useOwnerAccountType} from '#/state/classrooms/account'
import {
  type ClassroomPostAudience,
  sameAudience,
} from '#/state/classrooms/types'
import {type ComposerClassroomRef} from '#/state/shell/composer'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {postAudienceName} from '#/components/classrooms/postAudienceNames'
import {PostAudienceOptions} from '#/components/classrooms/PostAudienceOptions'
import * as Dialog from '#/components/Dialog'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'

/**
 * Who this post is for, asked of the post being written.
 *
 * The pill beside *Anyone can interact* says the answer, and this dialog is where
 * the answer is changed. It is a dialog of its own rather than a group inside
 * *Post interaction settings* — where the app's other questions about a post live
 * — because the two questions are not asked at the same time by the same person:
 * *who may reply* is a standing preference of an account, asked once and kept, and
 * *who may see this* is a decision about one post that an author changes and then
 * changes back. Wrapping the second inside the first meant opening the reply
 * settings to address a post, and it meant one checkbox doing two jobs — so each
 * question has a door of its own, and they share the room, the pill row and the
 * save they always shared.
 *
 * What it keeps is the same idea the interaction dialog keeps, said plainly: an
 * answer is kept **only when the author asks for it to be** (the checkbox), because
 * the person who narrowed one post has not said anything about the next one. Left
 * unticked, this post — and only this post — is narrowed, and the note above the
 * checkbox says what the next one will be instead.
 */
export function PostAudienceDialog({
  control,
  classroom,
  audience,
  audienceDefault,
  onChangeAudience,
  onPersistAudience,
}: {
  control: Dialog.DialogControlProps
  /** The room the post is being written in — whose it is, and how private. */
  classroom: ComposerClassroomRef
  /** The audience the post will be published with. */
  audience: ClassroomPostAudience
  /** The audience it would have been published with unasked. */
  audienceDefault: ClassroomPostAudience
  onChangeAudience: (audience: ClassroomPostAudience) => void
  /** Called when the author asked for this answer to be kept for next time. */
  onPersistAudience: (audience: ClassroomPostAudience) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  /*
   * The kind of account that runs the room, for naming it (*the school*, *the
   * tutor*) in the seats' own words — read here, once, and handed to the options.
   */
  const ownerAccountType = useOwnerAccountType(classroom.ownerDid)
  const [persist, setPersist] = useState(false)
  /*
   * *Changed* means changed against the answer this post would have had anyway —
   * the audience the author once asked to remember, or, when they never asked, the
   * room's own (the composer resolves that into `audienceDefault`). Compared as
   * audiences rather than as lists (`sameAudience`), because the two are chosen on
   * two different screens and only one of them got to put the seats in order.
   */
  const changed = !sameAudience(audience, audienceDefault)

  return (
    <Dialog.Outer control={control} nativeOptions={{preventExpansion: true}}>
      <Dialog.Handle />
      <Dialog.ScrollableInner
        label={l`Choose the audience of this post`}
        style={[web({maxWidth: 400}), a.w_full]}>
        <View style={[a.pb_lg]}>
          <Text style={[a.text_2xl, a.font_bold]}>
            <Trans>Audience</Trans>
          </Text>
        </View>
        <View style={[a.flex_1, a.gap_lg]}>
          <PostAudienceOptions
            ownerAccountType={ownerAccountType}
            audience={audience}
            onChange={onChangeAudience}
          />

          {/*
            The choice, and what becomes of it.

            Two answers, and which one is shown is the whole of the copy this
            dialog needs: an answer that differs from the post's own is *just this
            post* until the author says otherwise, and an answer that does not
            differ is already what the next post here will use. The safe direction
            is said out loud rather than left to be inferred from an unticked box,
            because the person who narrowed one post is exactly the person who has
            to know that the next one goes back.
          */}
          {changed ? (
            <View style={[a.gap_md]}>
              <Text
                testID="postAudienceThisTimeOnly"
                style={[
                  a.text_sm,
                  a.leading_snug,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  Just this post. Your next post here goes back to{' '}
                  {postAudienceName(l, audienceDefault)}.
                </Trans>
              </Text>
              <Toggle.Item
                name="persistAudience"
                type="checkbox"
                label={l`Save this audience for next time`}
                value={persist}
                onChange={() => setPersist(p => !p)}>
                <Toggle.Checkbox />
                <Toggle.LabelText
                  style={[a.text_md, a.font_normal, t.atoms.text]}>
                  {l`Save this audience for next time`}
                </Toggle.LabelText>
              </Toggle.Item>
            </View>
          ) : (
            <Text
              style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
              <Trans>
                Your next post in this classroom uses this audience.
              </Trans>
            </Text>
          )}

          <Button
            label={l`Save`}
            onPress={() => {
              if (persist) onPersistAudience(audience)
              control.close(() => setPersist(false))
            }}
            color="primary"
            size="large">
            <ButtonText>
              <Trans>Save</Trans>
            </ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
